import { find, head, map, each, clone } from 'lodash';
import moment from 'moment';

class PageController {

  constructor ($window, availabilityService, availabilityApiService, dateService, basket, itemService, checkout, storeService, $timeout, quantityService, loginoutService, configService, addGuestService, loadingService, imgix) {
    this.$window = $window;
    this.availabilityService = availabilityService;
    this.availabilityApiService = availabilityApiService;
    this.dateService = dateService;
    this.basket = basket;
    this.itemService = itemService;
    this.checkout = checkout;
    this.storeService = storeService;
    this.$timeout = $timeout;
    this.quantityService = quantityService;
    this.loginoutService = loginoutService;
    this.configService = configService;
    this.addGuestService = addGuestService;
    this.loadingService = loadingService;
    this.imgix = imgix; // Loads the factory so images auto-load in the storefront
  }

  $onInit () {
    this.loadingService.clearLoading();
    this.guests = this.guests || [];

    this.today = moment()
      .format('YYYY-MM-DD');

    this.category = this.category || '*';

    this.stopAvailability = this.configService.get('property').stopAvailability;

    if (this.item) {
      this.itemService.setItem(this.item);
    }

    this.determineAnnouncementState();
  }

  checkStoredDate () {
    this.storeService.set('searchDate', this.date);
    this.date = this.dateService.checkStoredDate(true);
  }

  checkDateExists () {
    const date = this.dateService.checkStoredDate(true);
    this.date = this.dateService.checkMaxDate(date, true)
      .format('YYYY-MM-DD');

    this.storeService.set('searchDate', this.date);
  }

  getStoredDate () {
    if (this.loginoutService.isEditing()) {
      return this.storeService.get('start_date');
    }

    return this.storeService.get('searchDate');
  }

  getAvailabilitySingle (checkDate = true) {
    this.loadingService.startLoading([ 'availability', 'times' ]);
    this.availabilityService.resetSingleDays();

    if (checkDate) {
      this.checkDateExists();
    }

    let toDate = moment(this.date, 'YYYY-MM-DD')
      .add(6, 'days')
      .format('YYYY-MM-DD');

    return this.basket.promise.then(() => {
      if (this.basket.contents.store_offer) {
        this.item.offer = this.basket.contents.store_offer;
      }

      return this.availabilityApiService.requestAvailability(this.date, toDate, this.eventType, this.item.id, this.item.offer || null)
        .then(response => {
          this.loadingService.stopLoading('availability');
          this.availability = response;

          if (this.availability
            && this.$window.fpInput
            && typeof this.$window.fpInput === 'object'
            && typeof this.$window.fpInput.setDate === 'function') {
            this.$window.fpInput.setDate(head(this.availability).date, false, 'Y-m-d');
          }
        });
    });
  }

  getAvailabilityList (shouldUpdateDate = false) {
    if (this.date && shouldUpdateDate) {
      this.storeService.set('searchDate', this.date);
    }
    this.date = this.dateService.checkStoredDate(shouldUpdateDate);

    if ((!this.availability && this.date)
      || (shouldUpdateDate && this.eventType)
      || (this.storeService.get('searchDate') && this.eventType)
    ) {
      this.startAvailability(shouldUpdateDate);

      if (this.stopAvailability) {
        return false;
      }

      if (this.eventType === 'category'
        || this.$window.location.pathname.indexOf('/search') > -1
        || this.$window.location.pathname.indexOf('/category') > -1) {
        return this.getCategoryAvailabilityList();
      }

      return this.requestAvailability(this.date, this.eventType, clone(this.eventIds))
        .then(response => {
          this.availability = {};
          this.availability[ this.eventType ] = response;

          this.$timeout(() => {
            this.updateIsotope(100);
          }, 0);
        });
    }
  }

  startAvailability (shouldUpdateDate) {
    if (shouldUpdateDate) {
      this.storeService.set('searchDate', this.date);
    }

    if (this.stopAvailability) {
      return false;
    }

    this.loadingService.startLoading('availability');
  }

  getCategoryAvailabilityList () {
    if (!this.date) {
      this.loadingService.stopLoading('availability');
      return;
    }

    if (this.stopAvailability) {
      this.loadingService.stopLoading('availability');
      return false;
    }

    let spadays = this.categoryDays;
    let treatments = this.categoryTreatments;

    this.availability = {};

    let promises = [];

    if (spadays && spadays.length) {
      promises.push(this.requestAvailability(this.date, 'day', clone(spadays))
        .then(response => {
          this.availability.day = response;
        }));
    }

    if (treatments && treatments.length) {
      promises.push(this.requestAvailability(this.date, 'treatment', clone(treatments))
        .then(response => {
          this.availability.treatment = response;
        }));
    }

    return Promise.all(promises)
      .then(() => {
        this.$timeout(() => {
          this.updateIsotope();
        }, 0);
      });
  }

  day () {
    if (this.item && this.item.date) {
      return moment(this.item.date, 'YYYY-MM-DD')
        .format('DD');
    }
  }

  month () {
    if (this.item && this.item.date) {
      return moment(this.item.date, 'YYYY-MM-DD')
        .format('MMM');
    }
  }

  hideBasket () {
    if (this.$window.location.href.indexOf('/payment') > -1) {
      return true;
    }

    return false;
  }

  resetBasket () {
    this.checkout.createAnalytics();
    this.storeService.set('basket', '');
    this.resetSearchDate();
    this.loginoutService.removeReservationData();
  }

  resetSearchDate () {
    this.storeService.remove('searchDate');
  }

  updateIsotope (delay = 1000) {
    this.loadingService.stopLoading('availability');
    this.$timeout(() => {
      this.updateIsotopeData();
    }, delay);
  }

  isEventUnavailable (eventId, eventType) {
    if (!this.loadingService.isLoading('availability') && this.availability && this.availability[ eventType ]) {
      let event = find(this.availability[ eventType ], event => event.id === eventId);
      return !event || !event.availability.available;
    }

    return false;
  }

  displayPriceFrom (eventId, eventType, basePrice, minimumGuests = 1) {
    return this.getPrice(eventId, eventType, basePrice, minimumGuests);
  }

  displayOriginalPrice (eventId, eventType, basePrice, minimumGuests = 1) {
    let event = this.getAvailableEvent(eventId, eventType);
    if (event && event.availability) {
      basePrice = event.availability.original_price;
    }

    return basePrice * minimumGuests;
  }

  getPrice (eventId, eventType, basePrice, minimumGuests = 1) {
    let event = this.getAvailableEvent(eventId, eventType);
    if (event) {
      let price = event.availability.price;
      if (price && price < basePrice) {
        basePrice = price;
      }
    }

    return basePrice * minimumGuests;
  }

  updateIsotopeData () {
    if (this.$window.isotope) {
      this.$window.isotope.forEach(container => {
        container.isotope.updateSortData();
        container.isotope.arrange({ sortBy: 'available' });
      });

      this.$timeout(() => {
        this.$window.dispatchEvent(new CustomEvent('imgixRefresh'));
      }, 250);
    }
  }

  categorySelected (type) {
    if (typeof this.eventType !== undefined && this.eventType === type) {
      return true;
    }

    return false;
  }

  presentModel (model) {
    return model.split('\\')[ 1 ].replace(/([A-Z])/g, ' $1')
      .trim();
  }

  displaySavedDate () {
    if (this.date) {
      return moment(this.date)
        .format('MMMM D, YYYY');
    }

    return moment()
      .format('MMMM D, YYYY');
  }

  offcanvasCollapsed () {
    return this.storeService.get('offcanvasCollapsed');
  }

  getAvailableEvent (eventId, eventType) {
    if (!this.loadingService.isLoading('availability') && this.availability && this.availability[ eventType ]) {
      return find(this.availability[ eventType ], { id: eventId });
    }
  }

  eventHasOffer (eventId, eventType, initialState = null) {
    if (initialState && !this.availability) {
      return initialState;
    }
    let event = this.getAvailableEvent(eventId, eventType);
    if (event && event.availability.offer) {
      return event.availability.offer.name;
    }
  }

  offerName (eventId, eventType, initialState = null) {
    if (initialState && !this.availability) {
      return initialState;
    }

    return this.eventHasOffer(eventId, eventType);
  }

  displayCross (display) {
    if (!this.loadingService.isLoading('availability') && this.availability) {
      let firstDay = head(this.availability);
      return display && !firstDay.available && !this.item.interacted;
    }
  }

  getQuantity (numberOfGuests = undefined) {
    this.quantity = undefined;
    let contentsQuantity = undefined;

    if (this.item.preferred_time) {
      let promises = [];

      this.loadingService.startLoading('quantity');
      if(this.integrationContents) {
        promises.push(this.quantityService.getContentsQuantity(this.item.date, this.item.preferred_time, this.item.end_time, this.integrationContents)
          .then(response => {
            contentsQuantity = response;
          }));
      }

      promises.push(this.quantityService.getQuantity(this.item, numberOfGuests)
        .then(response => {
          this.quantity = response.quantity;
          this.staff = map(response.staff, (staffId, staffName) => {
            return {
              id: staffId,
              label: staffName
            };
          }) || undefined;
        }));

      return Promise.all(promises)
        .then(() => {
          this.validateQuantity(contentsQuantity);

          this.$timeout(() => {
            this.loadingService.stopLoading('quantity');
          });
        });
    }
  }

  validateQuantity (contentsQuantity = undefined) {
    if (contentsQuantity !== undefined) {
      if (contentsQuantity !== true && !Number.isInteger(contentsQuantity)) {
        this.quantity = 0;
        return;
      }

      if (Number.isInteger(contentsQuantity)) {
        this.quantity = Math.min(this.quantity, contentsQuantity);
      }
    }

    if (this.item.minimum_guests > this.quantity) {
      this.quantity = 0;
      return;
    }

    this.quantity = Math.floor(this.quantity / this.item.minimum_guests) * this.item.minimum_guests;
  }

  resetStaff () {
    each(this.guests, (guest) => {
      delete (guest.staff);
    });

    each(this.staff, (staff) => {
      delete (staff.disabled);
    });
  }

  availableStaff () {
    return this.staff;
  }

  notEnoughGuests () {
    this.error = null;
    if (this.quantity && parseInt(this.item.minimum_guests) > parseInt(this.quantity)) {
      this.error = '<em>Sorry, there is currently no availability for the selected time.</em>';
      return true;
    }

    return false;
  }

  subtotalOriginal () {
    this.guests = this.addGuestService.getGuests();

    if (!this.guests.length) {
      return this.item.original_price;
    }

    return this.guests.length * this.item.original_price;
  }

  subtotalPrice () {
    this.guests = this.addGuestService.getGuests();
    
    if (!this.guests.length) {
      return this.item.minimum_guests * this.item.price;
    }

    return this.guests.length * this.item.price;
  }

  isLoginAllowed () {
    return this.loginoutService.isAllowed();
  }

  isLoggedIn () {
    return this.loginoutService.isLoggedIn();
  }

  isEditing () {
    return this.loginoutService.isEditing();
  }

  isCurrentPage (page) {
    var pathname = this.$window.location.pathname;

    return pathname.includes(page);
  }

  maybeRequestAvailability (response) {
    return new Promise(resolve => {
      if (response) {
        var pathname = this.$window.location.pathname;
        this.availability = null;

        this.checkDateExists();

        if (pathname.includes('/category') || pathname.includes('/search')) {
          return this.getCategoryAvailabilityList()
            .then(() => {
              resolve();
            });
        }

        if (pathname.includes('/treatments') || pathname.includes('/spa-days') || pathname.includes('/activities')) {
          return this.getAvailabilityList()
            .then(() => {
              resolve();
            });
        }

        if (pathname.includes('/treatment') || pathname.includes('/spa-day')) {
          return this.getAvailabilitySingle(false)
            .then(() => {
              resolve();
            });
        }
      }

      resolve();
    });
  }

  homeSearch () {
    let category = '';

    if (this.category) {
      this.category = this.category.replace('.', '');
      category = (this.category !== '*') ? `?category=${this.category}` : '';
    }

    switch (this.eventType) {
    case 'day':
      this.$window.location = `/spa-days${category}`;
      break;
    case 'treatment':
      this.$window.location = `/treatments${category}`;
      break;
    }
  }

  isAvailable (itemAvailable, telephoneEvent) {
    return itemAvailable && !telephoneEvent;
  }

  openSearch () {
    this.searchIsOpen = true;
  }

  closeSearch () {
    this.searchIsOpen = false;
  }

  requestAvailability (date, eventType, eventIds) {
    if (!eventIds || !eventIds.length) {
      return Promise.resolve();
    }

    return Promise.all(this.processAvailability(date, eventType, eventIds))
      .then(response => this.flattenResponse(response));
  }

  processAvailability (date, eventType, eventIds, promises = []) {
    if (!eventIds.length) {
      return promises;
    }

    let currentRequest = eventIds.splice(0, 20);

    promises.push(this.availabilityApiService.requestAvailabilityList(date, eventType, currentRequest));

    return this.processAvailability(date, eventType, eventIds, promises);
  }

  flattenResponse (arrayOfArrays) {
    return [].concat.apply([], arrayOfArrays);
  }

  noActivityAvailability () {
    if (!this.loadingService.isLoading('availability') && this.availability) {
      return !!this.availability.activity && !this.availability.activity.find(activity => activity.availability.available === true);
    }

    return false;
  }

  canRegister () {
    return !!(this.configService.get('login').canRegister && !(this.isLoggedIn() || this.isEditing()));
  }

  propertyConfig (value) {
    const config = this.configService.get('property');
    return !!config[ value ];
  }

  displaySpaDays () {
    if (this.showSpaDays !== undefined) {
      return this.showSpaDays;
    }

    this.showSpaDays = (this.propertyConfig('hasSpaDays') && !this.preventSpaDays()) ? 1 : 0;

    return this.showSpaDays;
  }

  preventSpaDays () {
    if (this.hideSpaDays !== undefined) {
      return this.hideSpaDays;
    }

    this.hideSpaDays = (this.isEditing() && this.configService.get('integration').preventEditSpadays) ? 1 : 0;

    return this.hideSpaDays;
  }

  openActivities () {
    return this.propertyConfig('openActivities');
  }

  buttonCount () {
    if (this.buttonCounter) {
      return this.buttonCounter;
    }

    let count = this.hasPromotedCategory ? 1 : 0;
    if (this.propertyConfig('hasTreatments')) {
      ++count;
    }

    if (this.displaySpaDays()) {
      ++count;
    }

    if (this.propertyConfig('hasActivities')
      && (this.isLoggedIn() || this.openActivities())) {
      ++count;
    }

    this.buttonCounter = count;
    return count;
  }

  setMaxSearchDate() {
    const searchDays = this.configService.get('property').maxActivitySearchDays;
    if(searchDays && searchDays > 0) {
      this.dateService.checkStoredDate(true);
      this.dateService.setCalendarParameter('maxDate', moment()
        .add(searchDays, 'days'));
    }
  }

  determineAnnouncementState() {
    this.announcementIsVisible = true;
    const hideAnnouncement = this.storeService.get('hideAnnouncement');
    if(hideAnnouncement) {
      if(moment(hideAnnouncement)
        .diff(moment(), 'days') >= 0) {
        this.announcementIsVisible = false;
        return;
      }
    }
  }

  hideAnnouncement() {
    this.storeService.set('hideAnnouncement', moment()
      .format('YYYY-MM-DD'));
    this.announcementIsVisible = false;
  }

  showAnnouncement() {
    this.storeService.remove('hideAnnouncement');
    this.announcementIsVisible = true;
  }
}

PageController.$inject = [ '$window', 'availabilityService', 'availabilityApiService', 'dateService', 'basket', 'itemService', 'checkout', 'storeService', '$timeout', 'quantityService', 'loginoutService', 'configService', 'addGuestService', 'loadingService', 'imgix' ];

export default PageController;



// WEBPACK FOOTER //
// ./frontend/storefront/app/modules/page/controllers/page.controller.js